/**
 * Data Access Layer for Users
 * 
 * Users are individual accounts within an organization/account.
 * Supports owner and admin roles with the same permissions,
 * except only owners can create new users.
 */

import { getClient, getAdminClient, hasAdminClient } from './client.js';
import { TABLES } from './tables.js';
import { hashNewPassword, verifyPassword } from './password.js';
import type {
  User,
  UserCreate,
  UserUpdate,
  UserInvite,
  UserInviteAccept,
  UserRole,
  UserSession,
  LoginResult,
} from './types.js';

// ============================================
// User Operations
// ============================================

export async function getUser(id: string): Promise<User | null> {
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Don't return password_hash
  const { password_hash, ...user } = data as User & { password_hash: string };
  return user;
}

export async function getUserByEmail(accountId: string, email: string): Promise<User | null> {
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('*')
    .eq('account_id', accountId)
    .eq('email', email.toLowerCase())
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Don't return password_hash
  const { password_hash, ...user } = data as User & { password_hash: string };
  return user;
}

export async function listUsers(accountId: string, filters?: {
  role?: UserRole;
  is_active?: boolean;
}): Promise<User[]> {
  let query = getClient()
    .from(TABLES.users)
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .eq('account_id', accountId);

  if (filters?.role) {
    query = query.eq('role', filters.role);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as User[];
}

export async function createUser(
  accountId: string,
  input: UserCreate,
  createdById?: string
): Promise<User> {
  const { salt, hash } = await hashNewPassword(input.password);
  
  const { data, error } = await getClient()
    .from(TABLES.users)
    .insert({
      account_id: accountId,
      email: input.email.toLowerCase(),
      name: input.name,
      role: input.role || 'admin',
      password_hash: hash,
      password_salt: salt,
      is_active: true,
      created_by: createdById || null,
    })
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}

export async function updateUser(id: string, input: UserUpdate): Promise<User> {
  const updateData: Record<string, unknown> = {};
  
  if (input.name !== undefined) updateData.name = input.name;
  if (input.role !== undefined) updateData.role = input.role;
  if (input.is_active !== undefined) updateData.is_active = input.is_active;
  if (input.password) {
    const { salt, hash } = await hashNewPassword(input.password);
    updateData.password_hash = hash;
    updateData.password_salt = salt;
    updateData.password_changed_at = new Date().toISOString();
  }

  const { data, error } = await getClient()
    .from(TABLES.users)
    .update(updateData)
    .eq('id', id)
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}

/**
 * Record that a user just logged in. Overwrites `last_login_at` in place — this is
 * current state ("last login"), not a discrete event, so it does NOT append an
 * auth_events row. Called fire-and-forget from the login handler, which runs under
 * service-role (bypasses RLS on the users table). Replaces the old per-request
 * `session_resumed` audit rows that flooded auth_events via the 30s verify poll.
 */
export async function updateUserLastLogin(userId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.users)
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', userId);

  if (error) throw error;
}

export async function getUserPasswordChangedAt(userId: string): Promise<string | null> {
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('password_changed_at')
    .eq('id', userId)
    .single();

  if (error || !data) return null;
  return data.password_changed_at || null;
}

export async function changeUserPassword(
  userId: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  // Fetch user with password fields
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('id, password_hash, password_salt')
    .eq('id', userId)
    .eq('is_active', true)
    .single();

  if (error || !data) {
    return { success: false, error: 'User not found' };
  }

  const isValid = await verifyPassword(currentPassword, data.password_salt, data.password_hash);
  if (!isValid) {
    return { success: false, error: 'Current password is incorrect' };
  }

  const { salt, hash } = await hashNewPassword(newPassword);
  const { error: updateError } = await getClient()
    .from(TABLES.users)
    .update({ password_hash: hash, password_salt: salt, password_changed_at: new Date().toISOString() })
    .eq('id', userId);

  if (updateError) throw updateError;
  return { success: true };
}

export async function deleteUser(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.users)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============================================
// User Invite Operations
// ============================================

function generateInviteToken(): string {
  // Generate a cryptographically secure random UUID token
  // Using globalThis.crypto.randomUUID() which works in both Node.js (19+) and browsers
  return globalThis.crypto.randomUUID();
}

export async function inviteUser(
  accountId: string,
  input: UserInvite,
  invitedById: string
): Promise<User> {
  const inviteToken = generateInviteToken();
  
  const { data, error } = await getClient()
    .from(TABLES.users)
    .insert({
      account_id: accountId,
      email: input.email.toLowerCase(),
      name: input.name,
      role: input.role || 'admin',
      password_hash: '', // Empty initially, will be set when accepting invite
      password_salt: '', // Empty initially
      is_active: true,
      invite_token: inviteToken,
      invited_at: new Date().toISOString(),
      invited_by: invitedById,
      created_by: invitedById,
    })
    .select('id, account_id, email, name, role, is_active, invite_token, invited_at, invited_by, accepted_at, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}

export async function getUserByInviteToken(token: string): Promise<User | null> {
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('*')
    .eq('invite_token', token)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Don't return password_hash
  const { password_hash, ...user } = data as User & { password_hash: string };
  return user;
}

export async function acceptInvite(
  inviteToken: string,
  input: UserInviteAccept
): Promise<User> {
  const { salt, hash } = await hashNewPassword(input.password);
  
  const { data, error } = await getClient()
    .from(TABLES.users)
    .update({
      password_hash: hash,
      password_salt: salt,
      accepted_at: new Date().toISOString(),
      invite_token: null, // Clear token after use
    })
    .eq('invite_token', inviteToken)
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}

export async function listPendingInvites(accountId: string): Promise<User[]> {
  const { data, error } = await getClient()
    .from(TABLES.users)
    .select('id, account_id, email, name, role, is_active, invited_at, invited_by, created_at, updated_at')
    .eq('account_id', accountId)
    .is('accepted_at', null)
    .not('invite_token', 'is', null)
    .order('invited_at', { ascending: false });

  if (error) throw error;
  return (data || []) as User[];
}

export async function cancelInvite(inviteToken: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.users)
    .delete()
    .eq('invite_token', inviteToken);

  if (error) throw error;
}

// ============================================
// Authentication Operations
// ============================================

export async function validateUserLogin(
  accountToken: string,
  email: string,
  password: string
): Promise<UserSession | null> {
  // First, get the account by token
  const { data: accounts, error: accountError } = await getClient()
    .from(TABLES.accounts)
    .select('id, slug, name, type')
    .eq('access_token', accountToken)
    .eq('is_active', true)
    .single();

  if (accountError || !accounts) {
    return null;
  }

  // Then, find the user by email in that account (need salt for verification)
  const { data: users, error: userError } = await getClient()
    .from(TABLES.users)
    .select('id, email, name, role, password_hash, password_salt')
    .eq('account_id', accounts.id)
    .eq('email', email.toLowerCase())
    .eq('is_active', true)
    .single();

  if (userError || !users) {
    return null;
  }

  // Verify password using PBKDF2
  const isValid = await verifyPassword(password, users.password_salt, users.password_hash);
  if (!isValid) {
    return null;
  }

  // Update last login
  await getClient()
    .from(TABLES.users)
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', users.id);

  return {
    user_id: users.id,
    user_email: users.email,
    user_name: users.name,
    user_role: users.role as UserRole,
    account_id: accounts.id,
    account_slug: accounts.slug,
    account_name: accounts.name,
    account_type: accounts.type,
  };
}

/**
 * Validate user login by email and password (JWT-based auth)
 * Finds user across all accounts by email
 * Returns detailed result including failure reasons for audit logging
 */
export async function validateUserLoginByEmail(
  email: string,
  password: string
): Promise<LoginResult> {
  // Find the user by email across all accounts (including inactive)
  const { data: users, error: userError } = await getClient()
    .from(TABLES.users)
    .select('id, account_id, email, name, role, is_active, password_hash, password_salt')
    .eq('email', email.toLowerCase())
    .single();

  if (userError || !users) {
    console.log('User lookup failed for email:', email, 'Error:', userError);
    return { success: false, reason: 'user_not_found' };
  }

  // Check if user is active
  if (!users.is_active) {
    console.log('User is inactive:', users.email);
    return {
      success: false,
      reason: 'user_inactive',
      userId: users.id,
      accountId: users.account_id,
    };
  }

  console.log('User found, verifying password. Salt exists:', !!users.password_salt, 'Hash exists:', !!users.password_hash);

  // Verify password using PBKDF2
  const isValid = await verifyPassword(password, users.password_salt, users.password_hash);
  console.log('Password verification result:', isValid);

  if (!isValid) {
    console.log('Password verification failed for user:', users.email);
    return {
      success: false,
      reason: 'invalid_password',
      userId: users.id,
      accountId: users.account_id,
    };
  }

  // Get the account details
  const { data: accounts, error: accountError } = await getClient()
    .from(TABLES.accounts)
    .select('id, slug, name, type, is_active, access_token')
    .eq('id', users.account_id)
    .single();

  if (accountError || !accounts) {
    console.log('Account lookup failed for account_id:', users.account_id, 'Error:', accountError);
    return { success: false, reason: 'user_not_found' };
  }

  // Check if account is active
  if (!accounts.is_active) {
    console.log('Account is inactive:', accounts.slug);
    return {
      success: false,
      reason: 'account_inactive',
      userId: users.id,
      accountId: accounts.id,
    };
  }

  // Update last login
  await getClient()
    .from(TABLES.users)
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', users.id);

  return {
    success: true,
    session: {
      user_id: users.id,
      user_email: users.email,
      user_name: users.name,
      user_role: users.role as UserRole,
      account_id: accounts.id,
      account_slug: accounts.slug,
      account_name: accounts.name,
      account_type: accounts.type,
    },
  };
}

export async function getUserSession(
  accountToken: string,
  userId: string
): Promise<UserSession | null> {
  // Get the account by token
  const { data: accounts, error: accountError } = await getClient()
    .from(TABLES.accounts)
    .select('id, slug, name, type')
    .eq('access_token', accountToken)
    .eq('is_active', true)
    .single();

  if (accountError || !accounts) {
    return null;
  }

  // Find the user in that account
  const { data: users, error: userError } = await getClient()
    .from(TABLES.users)
    .select('id, email, name, role')
    .eq('id', userId)
    .eq('account_id', accounts.id)
    .eq('is_active', true)
    .single();

  if (userError || !users) {
    return null;
  }

  return {
    user_id: users.id,
    user_email: users.email,
    user_name: users.name,
    user_role: users.role as UserRole,
    account_id: accounts.id,
    account_slug: accounts.slug,
    account_name: accounts.name,
    account_type: accounts.type,
  };
}

export function canUserCreateUsers(userRole: UserRole): boolean {
  return userRole === 'owner';
}

// ============================================
// Admin Operations (bypass RLS)
// ============================================

function requireAdminClient() {
  if (!hasAdminClient()) {
    throw new Error(
      'Admin client not available. Provide SUPABASE_SERVICE_ROLE_KEY to enable admin operations.'
    );
  }
  return getAdminClient()!;
}

export async function adminListUsers(accountId: string): Promise<User[]> {
  const client = requireAdminClient();
  const { data, error } = await client
    .from(TABLES.users)
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .eq('account_id', accountId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data || []) as User[];
}

export async function adminCreateUser(
  accountId: string,
  input: UserCreate,
  createdById?: string
): Promise<User> {
  const client = requireAdminClient();
  const { salt, hash } = await hashNewPassword(input.password);
  
  const { data, error } = await client
    .from(TABLES.users)
    .insert({
      account_id: accountId,
      email: input.email.toLowerCase(),
      name: input.name,
      role: input.role || 'admin',
      password_hash: hash,
      password_salt: salt,
      is_active: true,
      created_by: createdById || null,
    })
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}

export async function adminDeleteUser(id: string): Promise<void> {
  const client = requireAdminClient();
  const { error } = await client
    .from(TABLES.users)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Create the initial owner user for an account
 * Should only be called when setting up a new account
 */
export async function createOwnerUser(
  accountId: string,
  email: string,
  name: string,
  password: string
): Promise<User> {
  const { salt, hash } = await hashNewPassword(password);
  
  const { data, error } = await getClient()
    .from(TABLES.users)
    .insert({
      account_id: accountId,
      email: email.toLowerCase(),
      name,
      role: 'owner',
      password_hash: hash,
      password_salt: salt,
      is_active: true,
    })
    .select('id, account_id, email, name, role, is_active, last_login_at, created_by, created_at, updated_at')
    .single();

  if (error) throw error;
  return data as User;
}
